import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import './App.css';
// import { Layout } from './layout/Layout'; // Layout antigo com TopBar

// Rotas da aplicação original
import MainLayout from './layout/MainLayout'; // Usando MainLayout com Header

// Authentication
import Login from './pages/Login';

// Applications
import ApplicationsList from './pages/applications/ApplicationsList';
import ApplicationDetails from './pages/applications/ApplicationDetails';
import ApplicationCreate from './pages/applications/ApplicationCreate';
import ApplicationEdit from './pages/applications/ApplicationEdit';
import ParametersList from './pages/applications/ParametersList';
import ParameterCreate from './pages/applications/ParameterCreate';
import ParameterEdit from './pages/applications/ParameterEdit';
import PromptsList from './pages/applications/PromptsList';
import PromptCreate from './pages/applications/PromptCreate';
import PromptEdit from './pages/applications/PromptEdit';
import EmbeddingsList from './pages/applications/EmbeddingsList';
import ApplicationUsers from './pages/applications/ApplicationUsers';
import ToolsList from './pages/applications/ToolsList';
import ToolCreate from './pages/applications/ToolCreate';
import ToolEdit from './pages/applications/ToolEdit';

// Users
import UsersList from './pages/users/UsersList';
import UserDetails from './pages/users/UserDetails';
import UserChatHistory from './pages/users/UserChatHistory';
import UserSchedules from './pages/users/UserSchedules';
import ScheduleCreate from './pages/users/ScheduleCreate';
import ScheduleEdit from './pages/users/ScheduleEdit';
import UserDevices from './pages/users/UserDevices';
import DeviceCreate from './pages/users/DeviceCreate';
import DeviceDetails from './pages/users/DeviceDetails';

// Payments routes for user payment history and charge management
import UserPaymentsList from './pages/payments/UserPaymentsList';
import ChargeCreate from './pages/payments/ChargeCreate';
import ChargeDetails from './pages/payments/ChargeDetails';
import ChargesList from './pages/payments/ChargesList';

// Health
import {
  ProgramGroupsList,
  ProgramGroupDetails,
  ProgramGroupCreate,
  ProgramGroupUserAttach,
  ProgramGroupCalendarCreate,
  ProgramGroupCalendarEdit,
  ProgramGroupCalendarsList
} from './pages/health';

// Completions
import ChatInterface from './pages/completions/ChatInterface';

// Dashboard
import Dashboard from './pages/Dashboard';

// Logout
import LogoutPage from './pages/Logout';

// Componente ProtectedRoute adaptado para OIDC
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const auth = useAuth();
  
  if (auth.isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (!auth.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

// Simple callback component for OIDC authentication
const CallbackPage: React.FC = () => {
  const auth = useAuth();
  
  if (auth.isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        <p className="ml-3 text-blue-600">Completando autenticação...</p>
      </div>
    );
  }
  
  if (auth.error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="bg-red-50 p-6 rounded-lg border border-red-200 max-w-lg">
          <h2 className="text-xl font-semibold text-red-700 mb-2">Erro de Autenticação</h2>
          <p className="text-red-600">{auth.error.message}</p>
          <button 
            onClick={() => auth.signinRedirect()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }
  
  if (auth.isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return (
    <div className="flex items-center justify-center h-screen">
      <p>Redirecionando...</p>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Routes>
      {/* Login route */}
      <Route path="/login" element={<Login />} />
      
      {/* Callback route for OIDC authentication */}
      <Route path="/callback" element={<CallbackPage />} />
      
      {/* Logout route */}
      <Route path="/logout" element={<LogoutPage />} />
      
      {/* Protected application routes */}
      <Route
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        
        {/* Applications routes */}
        <Route path="/applications">
          <Route index element={<ApplicationsList />} />
          <Route path="create" element={<ApplicationCreate />} />
          <Route path=":id" element={<ApplicationDetails />} />
          <Route path=":id/edit" element={<ApplicationEdit />} />
          <Route path=":id/parameters">
            <Route index element={<ParametersList />} />
            <Route path="create" element={<ParameterCreate />} />
            <Route path=":parameterId/edit" element={<ParameterEdit />} />
          </Route>
          <Route path=":id/prompts">
            <Route index element={<PromptsList />} />
            <Route path="create" element={<PromptCreate />} />
            <Route path="edit/:promptId" element={<PromptEdit />} />
          </Route>
          <Route path=":id/tools">
            <Route index element={<ToolsList />} />
            <Route path="create" element={<ToolCreate />} />
            <Route path=":toolId/edit" element={<ToolEdit />} />
          </Route>
          <Route path=":id/embeddings" element={<EmbeddingsList />} />
          <Route path=":id/users" element={<ApplicationUsers />} />
        </Route>
        
        {/* Users routes */}
        <Route path="/users">
          <Route index element={<UsersList />} />
          <Route path=":id" element={<UserDetails />} />
          <Route path=":id/chat" element={<UserChatHistory />} />
          <Route path=":id/schedules">
            <Route index element={<UserSchedules />} />
            <Route path="create" element={<ScheduleCreate />} />
            <Route path=":scheduleId/edit" element={<ScheduleEdit />} />
          </Route>
          <Route path=":id/devices">
            <Route index element={<UserDevices />} />
            <Route path="create" element={<DeviceCreate />} />
            <Route path=":deviceId" element={<DeviceDetails />} />
          </Route>
          <Route path=":id/payments" element={<UserPaymentsList />} />
        </Route>
        
        {/* Charge management routes */}
        <Route path="/payments">
          <Route path="create" element={<ChargeCreate />} />
          <Route path="charges" element={<ChargesList />} />
          <Route path="charges/:id" element={<ChargeDetails />} />
        </Route>

        {/* Health Program Groups routes */}
        <Route path="/health/program-groups">
          <Route index element={<ProgramGroupsList />} />
          <Route path="create" element={<ProgramGroupCreate />} />
          <Route path=":id" element={<ProgramGroupDetails />} />
          <Route path=":id/users/attach" element={<ProgramGroupUserAttach />} />
          <Route path=":id/calendars">
            <Route index element={<ProgramGroupCalendarsList />} />
            <Route path="create" element={<ProgramGroupCalendarCreate />} />
            <Route path=":calendarId/edit" element={<ProgramGroupCalendarEdit />} />
          </Route>
        </Route>

        {/* Completions route for chat interface */}
        <Route path="/chat" element={<ChatInterface />} />

        {/* Fallback route */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  );
};

export default App;